import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import { logger } from "@/lib/logger";

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface BillingSettingsData {
    paymentTermDays: number;
    reminderEnabled: boolean;
    reminder1Days: number;
    reminder2Days: number;
    reminder3Days: number;
    reminderFeeCents: number;
    defaultGoaeFactor: number;
    invoiceNumberPrefix: string;
    bankName?: string;
    bankIban?: string;
    bankBic?: string;
    taxId?: string;
    vatId?: string;
}

export interface NotificationSettingsData {
    emailAppointment: boolean;
    emailMessages: boolean;
    emailBilling: boolean;
    pushNewPatient: boolean;
    pushUrgent: boolean;
    pushPhoneCalls: boolean;
}

export interface SecuritySettingsData {
    sessionTimeoutMinutes: number;
    twoFaEnabled: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// DEFAULTS
// ═══════════════════════════════════════════════════════════════════════════

export const DEFAULT_BILLING_SETTINGS: BillingSettingsData = {
    paymentTermDays: 30,
    reminderEnabled: true,
    reminder1Days: 14,
    reminder2Days: 28,
    reminder3Days: 42,
    reminderFeeCents: 500,
    defaultGoaeFactor: 2.3,
    invoiceNumberPrefix: "RE",
};

export const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettingsData = {
    emailAppointment: true,
    emailMessages: true,
    emailBilling: false,
    pushNewPatient: true,
    pushUrgent: true,
    pushPhoneCalls: true,
};

export const DEFAULT_SECURITY_SETTINGS: SecuritySettingsData = {
    sessionTimeoutMinutes: 60,
    twoFaEnabled: false,
};

function ensureSettingsPersistenceConfigured(): void {
    if (!isSupabaseConfigured()) {
        throw new Error("Supabase ist nicht konfiguriert. Einstellungen können nicht gespeichert werden.");
    }
}

// ═══════════════════════════════════════════════════════════════════════════
// BILLING SETTINGS (billing_settings table)
// ═══════════════════════════════════════════════════════════════════════════

export async function getBillingSettings(practiceId: string): Promise<BillingSettingsData> {
    if (!isSupabaseConfigured()) return DEFAULT_BILLING_SETTINGS;

    try {
        const { data, error } = await supabase
            .from("billing_settings")
            .select("*")
            .eq("practice_id", practiceId)
            .single();

        if (error) {
            if (error.code === "PGRST116") {
                // No row yet — return defaults
                return DEFAULT_BILLING_SETTINGS;
            }
            logger.warn("[settings.service] billing_settings fetch error:", error.message);
            return DEFAULT_BILLING_SETTINGS;
        }

        return {
            paymentTermDays: data.payment_term_days ?? 30,
            reminderEnabled: data.reminder_enabled ?? true,
            reminder1Days: data.reminder_1_days ?? 14,
            reminder2Days: data.reminder_2_days ?? 28,
            reminder3Days: data.reminder_3_days ?? 42,
            reminderFeeCents: data.reminder_fee_cents ?? 500,
            defaultGoaeFactor: data.default_goae_factor ?? 2.3,
            invoiceNumberPrefix: data.invoice_number_prefix ?? "RE",
            bankName: data.bank_name,
            bankIban: data.bank_iban,
            bankBic: data.bank_bic,
            taxId: data.tax_id,
            vatId: data.vat_id,
        };
    } catch (err) {
        logger.error("[settings.service] getBillingSettings error:", err);
        return DEFAULT_BILLING_SETTINGS;
    }
}

export async function saveBillingSettings(
    practiceId: string,
    settings: Partial<BillingSettingsData>,
): Promise<void> {
    ensureSettingsPersistenceConfigured();

    const dbData: Record<string, unknown> = {
        practice_id: practiceId,
        updated_at: new Date().toISOString(),
    };

    if (settings.paymentTermDays !== undefined) dbData.payment_term_days = settings.paymentTermDays;
    if (settings.reminderEnabled !== undefined) dbData.reminder_enabled = settings.reminderEnabled;
    if (settings.reminder1Days !== undefined) dbData.reminder_1_days = settings.reminder1Days;
    if (settings.reminder2Days !== undefined) dbData.reminder_2_days = settings.reminder2Days;
    if (settings.reminder3Days !== undefined) dbData.reminder_3_days = settings.reminder3Days;
    if (settings.reminderFeeCents !== undefined)
        dbData.reminder_fee_cents = settings.reminderFeeCents;
    if (settings.defaultGoaeFactor !== undefined)
        dbData.default_goae_factor = settings.defaultGoaeFactor;
    if (settings.invoiceNumberPrefix !== undefined)
        dbData.invoice_number_prefix = settings.invoiceNumberPrefix;
    if (settings.bankName !== undefined) dbData.bank_name = settings.bankName;
    if (settings.bankIban !== undefined) dbData.bank_iban = settings.bankIban;
    if (settings.bankBic !== undefined) dbData.bank_bic = settings.bankBic;
    if (settings.taxId !== undefined) dbData.tax_id = settings.taxId;
    if (settings.vatId !== undefined) dbData.vat_id = settings.vatId;

    const { error } = await supabase
        .from("billing_settings")
        .upsert(dbData, { onConflict: "practice_id" });

    if (error) {
        logger.error("[settings.service] saveBillingSettings error:", error.message);
        throw new Error("Abrechnungseinstellungen konnten nicht gespeichert werden");
    }
}

// ═══════════════════════════════════════════════════════════════════════════
// NOTIFICATION + SECURITY SETTINGS (practice_settings table, JSONB columns)
// ═══════════════════════════════════════════════════════════════════════════

export async function getNotificationSettings(
    practiceId: string,
): Promise<NotificationSettingsData> {
    if (!isSupabaseConfigured()) return DEFAULT_NOTIFICATION_SETTINGS;

    try {
        const { data, error } = await supabase
            .from("practice_settings")
            .select("reminders")
            .eq("practice_id", practiceId)
            .maybeSingle();

        if (error || !data?.reminders) return DEFAULT_NOTIFICATION_SETTINGS;

        // practice_settings.reminders stores notification prefs as JSONB
        const r = data.reminders as Record<string, unknown>;
        return {
            emailAppointment:
                (r.emailAppointment as boolean) ?? DEFAULT_NOTIFICATION_SETTINGS.emailAppointment,
            emailMessages:
                (r.emailMessages as boolean) ?? DEFAULT_NOTIFICATION_SETTINGS.emailMessages,
            emailBilling: (r.emailBilling as boolean) ?? DEFAULT_NOTIFICATION_SETTINGS.emailBilling,
            pushNewPatient:
                (r.pushNewPatient as boolean) ?? DEFAULT_NOTIFICATION_SETTINGS.pushNewPatient,
            pushUrgent: (r.pushUrgent as boolean) ?? DEFAULT_NOTIFICATION_SETTINGS.pushUrgent,
            pushPhoneCalls:
                (r.pushPhoneCalls as boolean) ?? DEFAULT_NOTIFICATION_SETTINGS.pushPhoneCalls,
        };
    } catch (err) {
        logger.error("[settings.service] getNotificationSettings error:", err);
        return DEFAULT_NOTIFICATION_SETTINGS;
    }
}

export async function saveNotificationSettings(
    practiceId: string,
    settings: NotificationSettingsData,
): Promise<void> {
    ensureSettingsPersistenceConfigured();

    const { error } = await supabase.from("practice_settings").upsert(
        {
            practice_id: practiceId,
            reminders: settings,
            updated_at: new Date().toISOString(),
        },
        { onConflict: "practice_id" },
    );

    if (error) {
        logger.error("[settings.service] saveNotificationSettings error:", error.message);
        throw new Error("Benachrichtigungseinstellungen konnten nicht gespeichert werden");
    }
}

export async function getSecuritySettings(practiceId: string): Promise<SecuritySettingsData> {
    if (!isSupabaseConfigured()) return DEFAULT_SECURITY_SETTINGS;

    try {
        const { data, error } = await supabase
            .from("practice_settings")
            .select("security")
            .eq("practice_id", practiceId)
            .maybeSingle();

        if (error || !data?.security) return DEFAULT_SECURITY_SETTINGS;

        // Use dedicated security column
        const sec = data.security as Record<string, unknown>;
        return {
            sessionTimeoutMinutes:
                (sec.sessionTimeoutMinutes as number) ??
                DEFAULT_SECURITY_SETTINGS.sessionTimeoutMinutes,
            twoFaEnabled: (sec.twoFaEnabled as boolean) ?? DEFAULT_SECURITY_SETTINGS.twoFaEnabled,
        };
    } catch (err) {
        logger.error("[settings.service] getSecuritySettings error:", err);
        return DEFAULT_SECURITY_SETTINGS;
    }
}

export async function saveSecuritySettings(
    practiceId: string,
    settings: SecuritySettingsData,
): Promise<void> {
    ensureSettingsPersistenceConfigured();

    // Read existing security data first, then merge
    let existingSecurity: Record<string, unknown> = {};
    try {
        const { data } = await supabase
            .from("practice_settings")
            .select("security")
            .eq("practice_id", practiceId)
            .maybeSingle();
        if (data?.security) existingSecurity = data.security as Record<string, unknown>;
    } catch {
        // No existing data — that's fine
    }

    const { error } = await supabase.from("practice_settings").upsert(
        {
            practice_id: practiceId,
            security: {
                ...existingSecurity,
                sessionTimeoutMinutes: settings.sessionTimeoutMinutes,
                twoFaEnabled: settings.twoFaEnabled,
            },
            updated_at: new Date().toISOString(),
        },
        { onConflict: "practice_id" },
    );

    if (error) {
        logger.error("[settings.service] saveSecuritySettings error:", error.message);
        throw new Error("Sicherheitseinstellungen konnten nicht gespeichert werden");
    }
}
